import { Injectable } from '@angular/core';
import { BehaviorSubject, EMPTY, Observable, Subject } from 'rxjs';
import { HttpClient, HttpRequest, HttpEventType, HttpResponse, HttpHeaders } from '@angular/common/http';
import { ParamMap } from '@angular/router';
import { catchError } from 'rxjs/operators';
import { AbstractControl, UntypedFormArray, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { EndpointDTO } from '../../../../system/system-dtos';
import { EncodingUtils } from '../../../../common/encoding-utils';
import { FileWrapper } from './entity-group-details.component';

@Injectable()
export class EntityGroupDetailsService {
  public reloadSubject: Subject<ParamMap> = new BehaviorSubject<ParamMap>(null);

  public isotopeLayout: Subject<void> = new BehaviorSubject<void>(null);

  constructor(private http: HttpClient) {}

  public performUpdate(endpoint: EndpointDTO, data: any): Observable<any> {
    return this.http.request(endpoint.method, endpoint.url.replace('{id}', data.id), {
      body: this.prepareBody(endpoint.fields, data),
    });
  }

  public performHttpPut(url: string, body: any): Observable<any> {
    return this.http.put(url, body);
  }

  public performHttpPost(url: string, body: any): Observable<any> {
    return this.http.post(url, body);
  }

  private prepareBody(fields: string[], data: any) {
    const body = {};
    fields.forEach((f) => {
      body[f] = data[f];
    });
    return body;
  }

  public submitMultipartForm(data: any, url: string, formGroup: UntypedFormGroup, method: string) {
    const wrapper = this.extractFile(formGroup) || ({} as any);

    const file: File = wrapper.file || undefined;
    const fieldName: string = wrapper.fieldName || undefined;
    data[fieldName] = file ? file.name : undefined;

    return this.uploadFile(url, fieldName, file, data, method);
  }

  private extractFile(formGroup: UntypedFormGroup): FileWrapper {
    return this.extractFileRecursive(null, formGroup, null);
  }

  private extractFileRecursive(
    controlName: string,
    control: AbstractControl,
    currentWrapper: FileWrapper
  ): FileWrapper {
    if (!control || currentWrapper) {
      return currentWrapper;
    }
    if (control instanceof UntypedFormGroup) {
      for (const key of Object.keys(control.controls)) {
        currentWrapper = this.extractFileRecursive(key, control.controls[key], currentWrapper);
      }
    } else if (control instanceof UntypedFormArray) {
      for (const nestedControl of control.controls) {
        currentWrapper = this.extractFileRecursive(null, nestedControl, currentWrapper);
      }
    } else if (control instanceof UntypedFormControl && control.value instanceof Set) {
      const files: Set<File> = control.value;
      if (files.size > 0) {
        files.forEach((f) => {
          currentWrapper = {
            fieldName: controlName,
            file: f,
          };
        });
      }
    }

    return currentWrapper;
  }

  public uploadFile(url: string, fieldName: string, file: File, data: any, method: string): Observable<any> {
    const formData: FormData = new FormData();

    if (file) {
      formData.append(fieldName, file, file.name);
    }
    formData.append('data', JSON.stringify(EncodingUtils.encodeRequest(data)));
    let headers = new HttpHeaders();
    // we add content type so encoding-interceptor can identify and not encode, we also delete header
    headers = headers.append('Content-Type', 'multipart/form-data');
    return this.http.request(method, url, { body: formData, headers });
  }

  public uploadFilesWithProgress(url: string, files: Set<File>): { [key: string]: { progress: Observable<number> } } {
    // this will be the our resulting map
    const status: { [key: string]: { progress: Observable<number> } } = {};

    files.forEach((file) => {
      // create a new multipart-form for every file
      const formData: FormData = new FormData();
      formData.append('file', file, file.name);

      // create a http-post request and pass the form
      // tell it to report the upload progress
      const req = new HttpRequest('POST', url, formData, {
        reportProgress: true,
      });

      // create a new progress-subject for every file
      const progress = new Subject<number>();

      // send the http-request and subscribe for progress-updates
      this.http.request(req).subscribe((event) => {
        if (event.type === HttpEventType.UploadProgress) {
          // calculate the progress percentage
          const percentDone = Math.round((100 * event.loaded) / event.total);

          // pass the percentage into the progress-stream
          progress.next(percentDone);
        } else if (event instanceof HttpResponse) {
          // Close the progress-stream if we get an answer form the API
          // The upload is complete
          progress.complete();
        }
      });

      // Save every progress-observable in a map of all observables
      status[file.name] = {
        progress: progress.asObservable(),
      };
    });

    // return the map of progress.observables
    return status;
  }
}
